
import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { PolicyProvider } from "./context/PolicyContext";
import { SidebarProvider } from "./components/sidebar";
import { SidebarInset } from "./components/sidebar/SidebarControls";
import AppSidebar from "./components/AppSidebar";
import Navbar from "./components/Navbar";
import { CancerProvider } from "./components/cancer/CancerProvider";

// Lazy-loaded components
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Employees = lazy(() => import("./pages/Employees"));
const EmployeeProfile = lazy(() => import("./pages/EmployeeProfile"));
const AddUser = lazy(() => import("./pages/AddUser"));
const LeaveRequest = lazy(() => import("./pages/LeaveRequest"));
const AdminLeave = lazy(() => import("./pages/AdminLeave"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Index = lazy(() => import("./pages/Index"));
const PolicySetup = lazy(() => import("./pages/PolicySetup"));
const PolicyView = lazy(() => import("./pages/PolicyView"));
const PolicyAcknowledgments = lazy(() => import("./pages/PolicyAcknowledgments"));
const Home = lazy(() => import("./pages/Home"));
const PatientLogin = lazy(() => import("./pages/PatientLogin"));
const PatientDiscussion = lazy(() => import("./pages/PatientDiscussion"));
const Patients = lazy(() => import("./pages/Patients"));
const SignificantEvents = lazy(() => import("./pages/SignificantEvents"));
const CancerDiagnoses = lazy(() => import("./pages/CancerDiagnoses"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Complaints = lazy(() => import("./pages/Complaints"));
const Compliments = lazy(() => import("./pages/Compliments"));
const PracticeMeetings = lazy(() => import("./pages/PracticeMeetings"));
const PracticeMeetingMinutes = lazy(() => import("./pages/PracticeMeetingMinutes"));
const MeetingAdminSettings = lazy(() => import("./pages/MeetingAdminSettings"));

// Wrapper component to handle redirect with parameters
const PolicyRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/policies/${id}`} replace />;
};

function App() {
  console.log("App rendering");
  
  return (
    <BrowserRouter>
      <AuthProvider>
        <PolicyProvider>
          <CancerProvider>
            <SidebarProvider defaultOpen={true}>
              <div className="flex min-h-screen w-full flex-col">
                <Navbar />
                <div className="flex flex-1 w-full">
                  <AppSidebar />
                  <SidebarInset>
                    <Suspense
                      fallback={
                        <div className="flex items-center justify-center min-h-screen flex-1">
                          <p className="text-lg">Loading...</p>
                        </div>
                      }
                    >
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/employees" element={<Employees />} />
                        <Route path="/employees/:id" element={<EmployeeProfile />} />
                        <Route path="/add-user" element={<AddUser />} />
                        <Route path="/leave" element={<LeaveRequest />} />
                        <Route path="/admin/leave" element={<AdminLeave />} />
                        <Route path="/policies/setup" element={<PolicySetup />} />
                        <Route path="/policies/:id" element={<PolicyView />} />
                        <Route path="/policy/:id" element={<PolicyRedirect />} />
                        <Route
                          path="/policies/acknowledgments"
                          element={<PolicyAcknowledgments />}
                        />
                        <Route path="/home" element={<Home />} />
                        <Route path="/patient-login" element={<PatientLogin />} />
                        <Route path="/patient-discussion" element={<PatientDiscussion />} />
                        <Route path="/patients" element={<Patients />} />
                        <Route path="/events" element={<SignificantEvents />} />
                        <Route path="/cancer-diagnoses" element={<CancerDiagnoses />} />
                        <Route path="/complaints" element={<Complaints />} />
                        <Route path="/compliments" element={<Compliments />} />
                        <Route path="/meetings" element={<PracticeMeetings />} />
                        <Route path="/meetings/:meetingId/minutes" element={<PracticeMeetingMinutes />} />
                        <Route path="/meetings/settings" element={<MeetingAdminSettings />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </SidebarInset>
                </div>
                <Toaster />
              </div>
            </SidebarProvider>
          </CancerProvider>
        </PolicyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
